import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { captureError } from '@/lib/sentry';

interface CustomField {
  id: string;
  label: string;
  data_type: string;
  value?: string;
}

interface ContactCustomFieldsProps {
  contactId: string;
  workspaceId: string;
}

const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'select', label: 'Select' }
];

export function ContactCustomFields({ contactId, workspaceId }: ContactCustomFieldsProps) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newField, setNewField] = useState({ label: '', data_type: 'text' });
  const { toast } = useToast();

  useEffect(() => {
    fetchCustomFields();
  }, [contactId, workspaceId]);

  const fetchCustomFields = async () => {
    try {
      // First get all custom fields for the workspace
      const { data: customFields, error: fieldsError } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: true });

      if (fieldsError) throw fieldsError;

      // Then get all values for this contact
      const { data: fieldValues, error: valuesError } = await supabase
        .from('custom_field_values')
        .select('*')
        .eq('contact_id', contactId);

      if (valuesError) throw valuesError;

      // Combine fields with their values
      const fieldsWithValues = customFields.map(field => ({
        ...field,
        value: fieldValues?.find(v => v.custom_field_id === field.id)?.value || ''
      }));

      setFields(fieldsWithValues);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      captureError(error as Error, { feature: 'contact-custom-fields-fetch' });
      toast({
        title: "Error",
        description: "Failed to load custom fields",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addField = async () => {
    if (!newField.label.trim()) return;

    try {
      const { error } = await supabase
        .from('custom_fields')
        .insert({
          workspace_id: workspaceId,
          label: newField.label.trim(),
          data_type: newField.data_type,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        description: "Custom field added successfully",
      });

      setNewField({ label: '', data_type: 'text' });
      setIsAdding(false);
      fetchCustomFields();
    } catch (error) {
      console.error('Error adding custom field:', error);
      captureError(error as Error, { feature: 'contacts-contact-custom-fields-add-field' });
      toast({
        title: "Error",
        description: "Failed to add custom field",
        variant: "destructive",
      });
    }
  };

  const updateFieldValue = async (fieldId: string, value: string) => {
    try {
      const { error } = await supabase
        .from('custom_field_values')
        .upsert({
          contact_id: contactId,
          custom_field_id: fieldId,
          value: value
        }, {
          onConflict: 'contact_id,custom_field_id'
        });

      if (error) throw error;

      // Update local state
      setFields(prev => prev.map(field => 
        field.id === fieldId ? { ...field, value } : field
      ));
    } catch (error) {
      console.error('Error updating field value:', error);
      captureError(error as Error, { feature: 'contacts-contact-custom-fields-update-field-value' });
      toast({
        title: "Error",
        description: "Failed to update field value",
        variant: "destructive",
      });
    }
  };

  const renderFieldInput = (field: CustomField) => {
    switch (field.data_type) {
      case 'text':
        return (
          <Input
            value={field.value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={field.value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={field.value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'boolean':
        return (
          <Switch
            checked={field.value === 'true'}
            onCheckedChange={(checked) => updateFieldValue(field.id, checked.toString())}
          />
        );
      default:
        return (
          <Input
            value={field.value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading custom fields...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Custom Fields</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsAdding(true)}
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Field
        </Button>
      </div>

      {isAdding && (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
          <Input
            placeholder="Field label"
            value={newField.label}
            onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
            className="h-7 text-xs"
          />
          <Select
            value={newField.data_type}
            onValueChange={(value) => setNewField(prev => ({ ...prev, data_type: value }))}
          >
            <SelectTrigger className="h-7 text-xs w-[120px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            size="sm" 
            onClick={addField}
            disabled={!newField.label.trim()}
            className="h-7 text-xs"
          >
            Add
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setIsAdding(false);
              setNewField({ label: '', data_type: 'text' });
            }}
            className="h-7 text-xs"
          >
            Cancel
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {fields.map((field) => (
          <div 
            key={field.id} 
            className="group flex items-center gap-4 p-2 rounded-md hover:bg-muted/50 transition-colors"
          >
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground">
                {field.label}
              </label>
              {renderFieldInput(field)}
            </div>
          </div>
        ))}

        {fields.length === 0 && !isAdding && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No custom fields yet. Add your first custom field above.
          </div>
        )}
      </div>
    </div>
  );
}